import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { TextField, AddressFields, PrivacyNotice } from "@/components/Fields";
import { MapPicker } from "@/components/MapPicker";
import { BigButton } from "@/components/Layout";
import { mobileInput, mobileProps, MobileHint } from "@/lib/india";
import { ChangeMobile } from "@/components/ChangeMobile";
import { VerifiedTick } from "@/components/VerifiedTick";

const KEYS = ["customer_type", "full_name", "business_name", "contact_person", "mobile", "alt_mobile", "contact_email",
  "address", "landmark", "area", "lat", "lng"];

export const ProfileForm = ({ onSaved, submitLabel, showPrivacy }) => {
  const { user, setUser, meta, t } = useApp();
  const [f, setF] = useState(() => {
    const init = Object.fromEntries(KEYS.map((k) => [k, user?.[k] ?? (k === "lat" || k === "lng" ? null : "")]));
    if (!init.full_name) init.full_name = user?.name || "";
    if (!init.contact_email) init.contact_email = user?.email || "";
    return init;
  });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const isBiz = f.customer_type && f.customer_type !== "Household";

  const save = async () => {
    if (!f.customer_type) return toast.error(t("Please choose who you are."));
    if (f.lat == null) return toast.error(t("Please set your location on the map."));
    setBusy(true);
    try {
      const { data } = await api.put("/profile", f);
      setUser(data);
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-sm font-semibold text-[#0D3B2B]">{t("Who are you?")}</p>
        <div className="grid grid-cols-2 gap-2">
          {(meta?.customer_types || []).map((ct) => (
            <button key={ct} type="button" onClick={() => set({ customer_type: ct })} data-testid={`ctype-${ct.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`h-12 rounded-2xl border-2 text-sm font-semibold transition-colors ${f.customer_type === ct ? "border-[#2E9E6B] bg-[#2E9E6B]/10 text-[#0D3B2B]" : "border-[#D2E0D8] bg-white text-[#4A6358]"}`}>
              {t(ct)}
            </button>
          ))}
        </div>
      </div>
      <TextField label={t("Full name")} testid="full-name-input" value={f.full_name} onChange={(e) => set({ full_name: e.target.value })} />
      {isBiz && (
        <>
          <TextField label={t("Business name")} testid="business-name-input" value={f.business_name} onChange={(e) => set({ business_name: e.target.value })} />
          <TextField label={t("Contact person")} testid="contact-person-input" value={f.contact_person} onChange={(e) => set({ contact_person: e.target.value })} />
        </>
      )}
      {user?.mobile ? (
        <div className="space-y-2" data-testid="mobile-locked">
          <TextField label={t("Mobile number")} testid="mobile-input" value={f.mobile} disabled readOnly />
          <div className="flex flex-wrap items-center justify-between gap-2"><VerifiedTick ok={user.mobile_verified} label={t("Mobile verified")} testid="onboard-mobile-verified" />
            <ChangeMobile current={user.mobile} t={t} onDone={(u) => { setUser(u); set({ mobile: u.mobile }); }} /></div>
        </div>
      ) : <>
      <TextField label={t("Mobile number")} testid="mobile-input" {...mobileProps} placeholder="98290 12345"
        value={f.mobile} onChange={(e) => set({ mobile: mobileInput(e.target.value) })} />
      <MobileHint value={f.mobile} />
      </>}
      <TextField label={t("Second contact number (optional)")} testid="alt-mobile-input" {...mobileProps} autoComplete="off"
        value={f.alt_mobile} onChange={(e) => set({ alt_mobile: mobileInput(e.target.value) })} />
      <MobileHint value={f.alt_mobile} />
      <TextField label={t("Email (optional)")} testid="contact-email-input" type="email" value={f.contact_email}
        onChange={(e) => set({ contact_email: e.target.value })} />
      <AddressFields form={f} set={set} />
      <TextField label={t("Area / locality")} testid="area-input" placeholder={t("e.g. C-Scheme, Malviya Nagar")}
        hint={t("Shown on the leaderboard instead of your address.")} value={f.area} onChange={(e) => set({ area: e.target.value })} />
      <div>
        <p className="mb-2 text-sm font-semibold text-[#0D3B2B]">{t("Your saved map location")}</p>
        <MapPicker lat={f.lat} lng={f.lng} autoDetect onChange={(lat, lng) => set({ lat, lng })} />
      </div>
      {showPrivacy && <PrivacyNotice />}
      <BigButton onClick={save} disabled={busy} data-testid="profile-save-btn">{busy ? t("Saving…") : submitLabel}</BigButton>
    </div>
  );
};

export default function Onboarding() {
  const { t } = useApp();
  return (
    <div className="mx-auto min-h-screen max-w-md px-4 pb-12 pt-8">
      <p className="text-xs font-bold uppercase tracking-widest text-[#2E9E6B]">{t("Welcome to Kindsaver")}</p>
      <h1 className="mb-1 mt-1 font-display text-3xl font-extrabold text-[#0D3B2B]">{t("Tell us about you")}</h1>
      <p className="mb-6 text-sm text-[#4A6358]">{t("We ask this only once. You can change it later in Profile.")}</p>
      <ProfileForm submitLabel={t("Save and continue")} showPrivacy />
    </div>
  );
}
